import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building2, Plus, Pencil, Trash2, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", head: "", description: "" };

export default function Departments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => { setLoading(true); api.get("/departments").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ name: d.name, head: d.head, description: d.description }); setEditId(d.id); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Department name is required"); return; }
    setSaving(true);
    try {
      if (editId) { await api.put(`/departments/${editId}`, form); toast.success("Department updated"); }
      else { await api.post("/departments", form); toast.success("Department created"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/departments/${deleteId}`); toast.success("Department deleted"); setDeleteId(null); load(); }
    catch (err) { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="departments-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground ashoka-underline inline-block">Departments</h2>
          <p className="text-muted-foreground mt-3">{rows.length} departments</p>
        </div>
        {isAdmin && (
          <Button data-testid="add-department-button" onClick={openAdd} className="bg-primary hover:bg-primary/90 text-primary-foreground">
            <Plus className="w-4 h-4 mr-1.5" /> Add Department
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {rows.map((d) => (
            <div key={d.id} data-testid={`department-card-${d.id}`} className="bg-card rounded-xl border border-border p-6 hover:-translate-y-0.5 transition-transform">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Building2 className="w-5 h-5" />
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button data-testid={`edit-department-${d.id}`} onClick={() => openEdit(d)} className="p-2 rounded-md hover:bg-secondary text-muted-foreground hover:text-primary transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-department-${d.id}`} onClick={() => setDeleteId(d.id)} className="p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground mt-4">{d.name}</h3>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{d.description || "No description"}</p>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border text-sm">
                <span className="text-muted-foreground">Head: <span className="text-foreground font-medium">{d.head || "—"}</span></span>
                <span className="flex items-center gap-1.5 text-primary font-semibold"><Users className="w-4 h-4" /> {d.employee_count}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="department-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? "Edit Department" : "Add Department"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name *</label>
              <Input data-testid="dept-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Head</label>
              <Input value={form.head} onChange={(e) => setForm({ ...form, head: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-department-button" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editId ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete department?</AlertDialogTitle>
            <AlertDialogDescription>This will remove the department. Employees will keep their assignment label.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-department-button" onClick={doDelete} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
